import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import Sidebar from "@/components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Users, 
  Phone, 
  PhoneIncoming, 
  ThumbsUp, 
  Calendar,
  TrendingUp,
  TrendingDown,
  Flame,
  Thermometer,
  Snowflake,
  BarChart3
} from "lucide-react";
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend
} from "recharts";

const Dashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchMetrics();
  }, []);

  const fetchMetrics = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/metrics`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setMetrics(response.data);
    } catch (error) {
      console.error("Failed to fetch metrics:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      console.error("Logout error:", e);
    }
    localStorage.removeItem("token");
    navigate("/");
  };

  const MetricCard = ({ title, value, icon: Icon, trend, trendValue, color = "indigo" }) => {
    const colorClasses = {
      indigo: "bg-indigo-100 text-indigo-600",
      emerald: "bg-emerald-100 text-emerald-600",
      amber: "bg-amber-100 text-amber-600",
      red: "bg-red-100 text-red-600",
      blue: "bg-blue-100 text-blue-600"
    };

    return (
      <Card className="border-slate-100 shadow-sm hover:shadow-md transition-shadow">
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <p className="text-sm text-slate-500">{title}</p>
              <p 
                className="text-3xl font-bold text-slate-900"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                {value}
              </p>
              {trend && (
                <div className={`flex items-center gap-1 text-sm ${
                  trend === "up" ? "text-emerald-600" : "text-red-500"
                }`}>
                  {trend === "up" ? (
                    <TrendingUp className="w-4 h-4" />
                  ) : (
                    <TrendingDown className="w-4 h-4" />
                  )}
                  {trendValue}
                </div>
              )}
            </div>
            <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
              <Icon className="w-5 h-5" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex">
        <Sidebar user={user} onLogout={handleLogout} />
        <main className="flex-1 ml-64 p-8">
          <div className="flex items-center justify-center h-full">
            <div className="spinner w-8 h-8 border-4 border-slate-200 border-t-indigo-600 rounded-full"></div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dashboard-page">
      <Sidebar user={user} onLogout={handleLogout} />
      
      <main className="ml-64 p-8">
        {/* Header */}
        <div className="mb-8 animate-fade-in">
          <h1 
            className="text-3xl font-bold text-slate-900"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Dashboard
          </h1>
          <p className="text-slate-500 mt-1">
            {user?.role === "manager" ? "Team Overview" : "Your Performance Overview"}
          </p>
        </div>

        {/* Metrics Grid - Bento Style */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="animate-fade-in stagger-1">
            <MetricCard 
              title="Total Leads"
              value={metrics?.total_leads || 0}
              icon={Users}
              color="indigo"
            />
          </div>
          <div className="animate-fade-in stagger-2">
            <MetricCard 
              title="Calls Made"
              value={metrics?.calls_made || 0}
              icon={Phone}
              color="blue"
            />
          </div>
          <div className="animate-fade-in stagger-3">
            <MetricCard 
              title="Interested"
              value={metrics?.interested || 0}
              icon={ThumbsUp}
              color="emerald"
            />
          </div>
          <div className="animate-fade-in stagger-4">
            <MetricCard 
              title="Meetings Booked"
              value={metrics?.meetings_booked || 0}
              icon={Calendar}
              color="amber"
            />
          </div>
        </div>

        {/* Second Row - Rates and Temperature */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Rates Card */}
          <Card className="lg:col-span-2 border-slate-100 shadow-sm animate-fade-in stagger-5">
            <CardHeader>
              <CardTitle 
                className="text-lg font-semibold text-slate-900"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Conversion Rates
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-6">
                <div className="text-center p-4 rounded-lg bg-slate-50">
                  <PhoneIncoming className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-slate-900">{metrics?.answer_rate || 0}%</p>
                  <p className="text-sm text-slate-500">Answer Rate</p>
                </div>
                <div className="text-center p-4 rounded-lg bg-slate-50">
                  <ThumbsUp className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-slate-900">{metrics?.interest_rate || 0}%</p>
                  <p className="text-sm text-slate-500">Interest Rate</p>
                </div>
                <div className="text-center p-4 rounded-lg bg-slate-50">
                  <Calendar className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-slate-900">{metrics?.meeting_rate || 0}%</p>
                  <p className="text-sm text-slate-500">Meeting Rate</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Lead Temperature */}
          <Card className="border-slate-100 shadow-sm animate-fade-in stagger-6">
            <CardHeader>
              <CardTitle 
                className="text-lg font-semibold text-slate-900"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Lead Temperature
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 rounded-lg bg-red-50">
                  <div className="flex items-center gap-3">
                    <Flame className="w-5 h-5 text-red-500" />
                    <span className="font-medium text-slate-700">Hot</span>
                  </div>
                  <span className="text-xl font-bold text-red-600">{metrics?.hot || 0}</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-amber-50">
                  <div className="flex items-center gap-3">
                    <Thermometer className="w-5 h-5 text-amber-500" />
                    <span className="font-medium text-slate-700">Warm</span>
                  </div>
                  <span className="text-xl font-bold text-amber-600">{metrics?.warm || 0}</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-blue-50">
                  <div className="flex items-center gap-3">
                    <Snowflake className="w-5 h-5 text-blue-500" />
                    <span className="font-medium text-slate-700">Cold</span>
                  </div>
                  <span className="text-xl font-bold text-blue-600">{metrics?.cold || 0}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Daily Activity Chart */}
          <Card className="border-slate-100 shadow-sm animate-fade-in">
            <CardHeader>
              <CardTitle 
                className="text-lg font-semibold text-slate-900 flex items-center gap-2"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                <BarChart3 className="w-5 h-5 text-indigo-500" />
                Daily Activity (Last 30 Days)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                {metrics?.daily_data && metrics.daily_data.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={metrics.daily_data}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 12, fill: '#64748B' }}
                        tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      />
                      <YAxis tick={{ fontSize: 12, fill: '#64748B' }} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'white', 
                          border: '1px solid #E2E8F0',
                          borderRadius: '8px'
                        }}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="calls" 
                        stroke="#4F46E5" 
                        fill="#EEF2FF" 
                        name="Calls"
                      />
                      <Area 
                        type="monotone" 
                        dataKey="texts" 
                        stroke="#10B981" 
                        fill="#D1FAE5" 
                        name="Texts"
                      />
                      <Area 
                        type="monotone" 
                        dataKey="meetings" 
                        stroke="#F59E0B" 
                        fill="#FEF3C7" 
                        name="Meetings"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-slate-400">
                    <p>No activity data yet</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Status Distribution */}
          <Card className="border-slate-100 shadow-sm animate-fade-in">
            <CardHeader>
              <CardTitle 
                className="text-lg font-semibold text-slate-900"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Lead Status Distribution
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                {metrics?.status_counts && Object.keys(metrics.status_counts).length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart 
                      data={Object.entries(metrics.status_counts).map(([status, count]) => ({
                        status,
                        count
                      }))}
                      layout="vertical"
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis type="number" tick={{ fontSize: 12, fill: '#64748B' }} />
                      <YAxis 
                        dataKey="status" 
                        type="category" 
                        width={120}
                        tick={{ fontSize: 12, fill: '#64748B' }}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'white', 
                          border: '1px solid #E2E8F0',
                          borderRadius: '8px'
                        }}
                      />
                      <Bar dataKey="count" fill="#4F46E5" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-slate-400">
                    <p>No status data yet</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
